import { useState } from 'react';

export type CallType = 'solution' | 'knowledge' | 'destruction';
export type CallStep = 
  | 'channel' 
  | 'type' 
  | 'area' 
  | 'cfkConnection'   // CfK only: connected to CfS?
  | 'cfkBounty'       // CfK only: does the CfS have bounty?
  | 'bounty' 
  | 'bountyDetails' 
  | 'details' 
  | 'roles' 
  | 'review' 
  | 'complete';

export interface CallFormData {
  sourceType: 'channel' | 'personal' | null;
  sourceChannelId: string | null;
  channelName: string;
  type: CallType | null;
  functionalArea: string;
  functionalAreaName: string;
  // CfK-specific fields
  connectedToCfS: boolean;
  cfSHasBounty: boolean;
  linkedCfSId: string | null;
  // Bounty fields
  isBounty: boolean;
  bountyAmount: string;
  bountyType: string;
  deadline: string;
  escrowConditions: string;
  releaseConditions: string;
  // Call details
  title: string;
  description: string;
  requirements: string;
  objectives: string;
  evaluationCriteria: string;
  // Role assignments
  creator: string;
  creatorId: string | null;
  contributors: string[];
  contributorIds: string[];
  confirmer: string;
  confirmerId: string | null;
  composer: string;
  composerId: string | null;
  // Attribution percentages (for bounty)
  creatorPercentage: string;
  contributorPercentage: string;
  confirmerPercentage: string;
  composerPercentage: string;
}

export interface CallCreationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  step?: CallStep;
  metadata?: any;
}

const INITIAL_FORM_DATA: CallFormData = {
  sourceType: null,
  sourceChannelId: null,
  channelName: '',
  type: null,
  functionalArea: '',
  functionalAreaName: '',
  connectedToCfS: false,
  cfSHasBounty: false,
  linkedCfSId: null,
  isBounty: false,
  bountyAmount: '',
  bountyType: '',
  deadline: '',
  escrowConditions: '',
  releaseConditions: '',
  title: '',
  description: '',
  requirements: '',
  objectives: '',
  evaluationCriteria: '',
  creator: '',
  creatorId: null,
  contributors: [],
  contributorIds: [],
  confirmer: '',
  confirmerId: null,
  composer: '',
  composerId: null,
  creatorPercentage: '40',
  contributorPercentage: '30',
  confirmerPercentage: '10',
  composerPercentage: '20',
};

// Get the correct step sequence based on call type and selections
const getStepSequence = (formData: CallFormData): CallStep[] => {
  const steps: CallStep[] = ['channel', 'type', 'area'];
  
  // CfK-specific steps: connection questions come after area selection
  if (formData.type === 'knowledge') {
    steps.push('cfkConnection');
    if (formData.connectedToCfS) {
      steps.push('cfkBounty');
    }
  }
  
  // Bounty step for all call types
  steps.push('bounty');
  
  // Bounty details if bounty selected
  if (formData.isBounty) {
    steps.push('bountyDetails');
  }
  
  // Common final steps
  steps.push('details', 'roles', 'review', 'complete');
  
  return steps;
};

export const useCallCreationFlow = (initialCallType?: CallType | null) => {
  // ALWAYS start with channel selection - this is the unified flow
  const [currentStep, setCurrentStep] = useState<CallStep>('channel');
  const [formData, setFormData] = useState<CallFormData>({
    ...INITIAL_FORM_DATA,
    type: initialCallType || null,
  });
  const [conversationMessages, setConversationMessages] = useState<CallCreationMessage[]>([]);

  const updateFormData = (field: keyof CallFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addMessage = (message: Omit<CallCreationMessage, 'id'>) => {
    const newMessage: CallCreationMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random()}`,
    };
    setConversationMessages(prev => [...prev, newMessage]);
  };

  const progressToNextStep = () => {
    const steps = getStepSequence(formData);
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const goToStep = (step: CallStep) => {
    setCurrentStep(step);
  };

  const getNextStep = (): CallStep | null => {
    const steps = getStepSequence(formData);
    const currentIndex = steps.indexOf(currentStep);
    return currentIndex < steps.length - 1 ? steps[currentIndex + 1] : null;
  };

  const reset = () => {
    setCurrentStep('channel');
    setFormData({ ...INITIAL_FORM_DATA, type: initialCallType || null });
    setConversationMessages([]);
  };

  return {
    currentStep,
    formData,
    conversationMessages,
    updateFormData,
    addMessage,
    progressToNextStep,
    goToStep,
    getNextStep,
    setCurrentStep,
    reset,
  };
};
